// src/pages/Transactions.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Table, { Column } from "../../components/Table";
import Button from "../../components/Button";
import Modal from "../../components/Modal";
import {
  getTransactions,
  getTransactionReceipt,
  ApiTransaction,
} from "../../services/loanService";
import InputField from "../../components/InputField";
import { Icon } from "@iconify/react";
import axiosClient from "../../api/axiosClient";
import toast from "react-hot-toast";

type TransactionRow = {
  transaction_id: number;
  loanId?: string | number;
  loan_code?: string | number;
  userId?: string | number;
  processedbyrole?: string | number | null;
  loanAmount?: number;
  amountPaid?: number;
  paymentMode?: string | null;
  referenceNumber?: string | null;
  status?: string | null;
  createdAt?: string | null;
  apiRaw?: ApiTransaction;
  processedbyid?: string | number | null;
  mobileHeader: string;
  emi_amount?: number;
  loanstatus?: string | null;
};

const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
});

export default function Transactions() {
  const [data, setData] = useState<TransactionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // modal state for "View Raw"
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTx, setModalTx] = useState<ApiTransaction | null>(null);

  // search state
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const debounceRef = useRef<number | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<string>("");
  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTx, setEditTx] = useState<ApiTransaction | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [displayAmount, setDisplayAmount] = useState("");

  const mapApiToRow = (t: ApiTransaction): TransactionRow => {
    const loanAmount = t.loan?.loan_amount ?? t.loan?.disbursed_amount ?? null;
    return {
      transaction_id: t.transaction_id,
      loanId: t.loan?.loan_id ?? t.loan_id,
      loan_code:
        t.loan?.loan_code ??
        t.loan?.loan_code ??
        (t.loan_id ? `LN-${t.loan_id}` : undefined),
      userId: t.user_id,
      processedbyrole: t.processed_by_role ?? null,
      processedbyid: t.processed_by_id ?? null,
      loanAmount: loanAmount ? Number(loanAmount) : undefined,
      emi_amount: t.loan?.emi_amount ? Number(t.loan.emi_amount) : undefined,
      amountPaid: t.amount_paid
        ? Number(parseFloat(String(t.amount_paid)))
        : undefined,
      paymentMode: t.payment_mode ?? null,
      referenceNumber: t.reference_number ?? null,
      status: t.status ?? null,
      createdAt: t.created_at ?? null,
      apiRaw: t,
      loanstatus: t.loan?.status,
      mobileHeader: `Loan Code: ${t.loan?.loan_code ?? "-"} | loanAmount: ${loanAmount ?? "-"}`,
    };
  };

  // Fetch: either call search endpoint if query present, otherwise fallback to getTransactions()
  const fetchAll = useCallback(
    async (query?: string, paymentF?: string) => {
      setLoading(true);
      try {
        const useSearch =
          (query && query.length > 0) || (paymentF && paymentF !== "");

        if (useSearch) {
          const params: any = {};
          if (query) params.query = query;
          if (paymentF && paymentF !== "") params.payment_mode = paymentF;
          const res = await axiosClient.get("/transactions/search", { params });
          const payload = res.data ?? {}; // support shapes: { transactions: [...] }, { data: [...] }, or plain array
          const rawTxs: ApiTransaction[] = Array.isArray(payload.transactions)
            ? payload.transactions
            : Array.isArray(payload.data)
              ? payload.data
              : Array.isArray(payload)
                ? payload
                : [];
          const rows = rawTxs.map(mapApiToRow);
          setData(rows);
        } else {
          // no query -> fallback to default listing
          const resp = await getTransactions(); // expected { transactions: [...] }
          const rows = (resp.transactions || []).map(mapApiToRow);
          setData(rows);
        }
      } catch (err: any) {
        console.error("Failed to fetch transactions", err);
        setData([]);
        // lightweight user feedback; keep existing alert pattern
        alert(err?.message ?? "Failed to load transactions.");
      } finally {
        setLoading(false);
      }
    },
    [], // mapApiToRow is stable here (declared above) and axiosClient/getTransactions are external
  );

  useEffect(() => {
    // initial load
    fetchAll("", paymentFilter);
  }, [fetchAll, paymentFilter]);

  // debounce searchTerm -> debouncedSearch
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // trigger fetch when debouncedSearch updates
  useEffect(() => {
    fetchAll(debouncedSearch, paymentFilter);
  }, [debouncedSearch, paymentFilter, fetchAll]);

  // immediate apply on Enter
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setDebouncedSearch(searchTerm.trim());
    }
  };

  /* ---------------- FIRST ROW ID ---------------- */

  const firstTransactionId = useMemo(() => {
    return data.length > 0 ? data[0].transaction_id : null;
  }, [data]);

  // User-provided download method integrated here (adapted slightly to local scope)
  const handleDownloadReceipt = useCallback(
    async (tx: ApiTransaction | null) => {
      if (!tx) return;
      setDownloadingId(tx.transaction_id); // mark which one is downloading
      try {
        const res = await getTransactionReceipt(tx.transaction_id);
        if (!res.success)
          throw new Error(res.message ?? "Failed to download receipt");

        const blob = res.blob as Blob;
        const filenameFromHeader = (() => {
          try {
            const cd =
              res.headers?.["content-disposition"] ||
              res.headers?.["Content-Disposition"] ||
              "";
            const m1 = /filename\*=UTF-8''(.+)$/.exec(cd);
            if (m1 && m1[1]) return decodeURIComponent(m1[1]);
            const m2 = /filename="?([^\";]+)"?/.exec(cd);
            if (m2 && m2[1]) return m2[1];
          } catch {}
          return `receipt_${tx.transaction_id}.pdf`;
        })();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filenameFromHeader || `receipt_${tx.transaction_id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        console.error(err);
        alert(err?.message ?? "Failed to download receipt");
      } finally {
        setDownloadingId(null); // reset
      }
    },
    [],
  );

  const openRawModal = (tx: ApiTransaction) => {
    setModalTx(tx);
    setModalOpen(true);
  };

  const openEditModal = (tx: ApiTransaction) => {
    setEditTx(tx);
    setEditAmount(String(tx.amount_paid ?? ""));
    setDisplayAmount(String(tx.amount_paid ?? ""));
    setEditModalOpen(true);
  };

  const handleUpdateAmount = async () => {
    if (!editTx) return;
    setEditLoading(true);
    try {
      await axiosClient.post(`/transactions/update/${editTx.transaction_id}`, {
        amount: Number(editAmount),
      });
      toast.success("Updated successfully");
      setEditModalOpen(false);
      fetchAll(debouncedSearch, paymentFilter);
    } finally {
      setEditLoading(false);
    }
  };

  const columns: Column<TransactionRow>[] = useMemo(
    () => [
      {
        header: "",
        accessor: "mobileHeader",
        sortable: false,
        cell: (r) => (
          <div className="sm:hidden">
            <div className="font-semibold text-sm">{r.mobileHeader}</div>
          </div>
        ),
        // width: "",
      },
      {
        header: "Loan Code",
        accessor: "loan_code",
        sortable: true,
        cell: (r) => <div className="font-mono">{r.loan_code ?? "-"}</div>,
      },
      { header: "Processed By", accessor: "processedbyrole", sortable: true },
      { header: "Processed ID", accessor: "processedbyid", sortable: true },
      {
        header: "Loan Amount",
        accessor: "loanAmount",
        sortable: true,
        cell: (r) => (r.loanAmount ? currency.format(r.loanAmount) : "-"),
      },
      {
        header: "Due Amount",
        accessor: "emi_amount",
        sortable: true,
        cell: (r) => (r.emi_amount ? currency.format(r.emi_amount) : "-"),
      },
      {
        header: "Amount Paid",
        accessor: "amountPaid",
        sortable: true,
        cell: (r) => (r.amountPaid ? currency.format(r.amountPaid) : "-"),
      },
      {
        header: "Payment Mode",
        accessor: "paymentMode",
        sortable: true,
        cell: (r) => r.paymentMode ?? "-",
      },
      {
        header: "Status",
        accessor: "status",
        sortable: true,
        cell: (row) => (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${row.status === "Success" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
          >
            {row.status}
          </span>
        ),
      },
      {
        header: "Created At",
        accessor: "createdAt",
        sortable: true,
        cell: (r) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString("en-IN") : "-",
      },
      {
        header: "Actions",
        accessor: "actions",
        sortable: false,
        cell: (r: any) => (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className=""
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (r.apiRaw) handleDownloadReceipt(r.apiRaw);
              }}
              disabled={downloadingId === r.transaction_id}
            >
              {downloadingId === r.transaction_id ? (
                <Icon
                  style={{ color: "blue" }}
                  icon="line-md:download"
                  width="24"
                  height="24"
                ></Icon>
              ) : (
                <Icon icon="line-md:download" width="24" height="24"></Icon>
              )}

              {/* {downloadingId === r.transaction_id ? "Downloading..." : "Download"} */}
            </Button>

            <Button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (r.apiRaw) openRawModal(r.apiRaw);
              }}
            >
              <Icon icon="basil:eye-solid" width="24" height="24"></Icon>
            </Button>
            {/* ✅ EDIT ONLY FOR LAST TRANSACTION */}
            {r.transaction_id === firstTransactionId &&
              r.loanstatus != "Settlement" && (
                <Button onClick={() => openEditModal(r.apiRaw)}>
                  <Icon
                    icon="line-md:edit-filled"
                    width="24"
                    height="24"
                  ></Icon>
                </Button>
              )}
          </div>
        ),
      },
    ],
    [downloadingId, handleDownloadReceipt, openRawModal],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalTx(null);
  }, []);
  const formatDateTime = (isoDate: string) => {
    if (!isoDate) return "";

    const date = new Date(isoDate);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12 || 12; // 12-hour format

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  };

  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center bg-white p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold">Transactions</h2>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="border border-gray-200 shadow-sm rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:outline-none transition"
          >
            <option value="">All</option>
            <option value="cash">Cash</option>
            <option value="upi">UPI</option>
          </select>
        </div>

        <div className="">
          {/* Search Input */}
          <div className="relative w-full ">
            <InputField
              placeholder="Loan Code (eg: LN012...)"
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      <Table<TransactionRow>
        // title="Loan Transactions"
        data={data}
        columns={columns}
        defaultPageSize={10}
        pageSizeOptions={[5, 10, 25]}
        mobileHeaderFields={["mobileHeader"]}
        loading={loading}
      />

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={
          modalTx ? `Transaction ${modalTx.transaction_id}` : "Transaction"
        }
      >
        {modalTx ? (
          <div className="space-y-3 text-sm grid grid-cols-1 md:grid-cols-2">
            <div className="">
              <div className="mb-2">
                <strong>Transaction ID:</strong> {modalTx.transaction_id}
              </div>
              <div className="mb-2">
                <strong>Loan Code:</strong> {modalTx.loan?.loan_code}
              </div>
              {/* <div className="mb-2"><strong>Schedule ID:</strong> {modalTx.schedule_id ?? "-"}</div> */}
              {/* <div className="mb-2"><strong>User ID:</strong> {modalTx.user_id}</div> */}
              <div className="mb-2">
                <strong>Processed By:</strong> {modalTx.processed_by_id ?? "-"}
              </div>
              <div className="mb-2">
                <strong>Processed By (role):</strong>{" "}
                {modalTx.processed_by_role ?? "-"}
              </div>
            </div>
            <div className="">
              <div className="mb-2">
                <strong>Amount Paid:</strong> {modalTx.amount_paid}
              </div>
              <div className="mb-2">
                <strong>Additional Amount:</strong> {modalTx.additional_amount}
              </div>
              <div className="mb-2">
                <strong>Adjustment Amount:</strong> {modalTx.adjustment_amount}
              </div>
              <div className="mb-2">
                <strong>Payment Mode:</strong> {modalTx.payment_mode ?? "-"}
              </div>
              <div className="mb-2">
                <strong>Status:</strong> {modalTx.status ?? "-"}
              </div>
              <div className="mb-2">
                <strong>Created At:</strong>{" "}
                {formatDateTime(modalTx.created_at ?? "-")}
              </div>
            </div>
          </div>
        ) : (
          <div>No data</div>
        )}
      </Modal>

      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Transaction"
      >
        <div className="flex font-semibold mb-4">
          <label>Paid Amount :</label>
          <h6>{displayAmount}</h6>
        </div>
        <label>Edit Amount :</label>
        <InputField
          type="number"
          value={editAmount}
          onChange={(e) => setEditAmount(e.target.value)}
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateAmount} disabled={editLoading}>
            Submit
          </Button>
        </div>
      </Modal>
    </div>
  );
}
